import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { ChartBar, TrendUp, CurrencyDollar, UsersThree, Lightning, Bank, Wallet } from '@phosphor-icons/react';

const COLORS = ['#FF6B35', '#00E5FF', '#22C55E', '#A855F7', '#F59E0B', '#EC4899', '#3B82F6', '#EF4444'];

const ChartTooltipContent = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 shadow-xl backdrop-blur-xl">
      <p className="text-xs text-neutral-400 mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('en-IN') : p.value}
        </p>
      ))}
    </div>
  );
};

export default function AnalyticsPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try { const res = await api.get('/reports/analytics'); setData(res.data); } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, [api]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="text-center py-20 text-neutral-500">Failed to load analytics</div>;

  const cs = data.currency || 'Rs.';

  return (
    <div data-testid="analytics-page" className="space-y-6 relative">
      <div className="glow-orb-orange -top-20 right-0 z-0" />
      <div className="glow-orb-teal top-60 -left-20 z-0" />

      <div className="relative z-10">
        <p className="overline">Insights</p>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight mt-1">Analytics & Charts</h1>
        <p className="text-sm text-neutral-500 mt-1">Visual overview of your household data</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 relative z-10">
        {/* Expense Trend */}
        <div className="glass-card p-6 animate-fade-up" data-testid="chart-expense-trend">
          <div className="flex items-center gap-2 mb-5">
            <TrendUp size={18} weight="duotone" className="text-[#FF6B35]" />
            <span className="text-sm font-semibold text-white">Expense Trend</span>
          </div>
          {data.expense_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.expense_trend}>
                <defs>
                  <linearGradient id="grdExpense" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#ffffff08" />
                <XAxis dataKey="label" tick={{ fill: '#737373', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#737373', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `${v / 1000}k`} />
                <Tooltip content={<ChartTooltipContent />} />
                <Area type="monotone" dataKey="total" stroke="#FF6B35" strokeWidth={2} fill="url(#grdExpense)" name={`Total (${cs})`} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-neutral-500 text-sm text-center py-10">No expense data available</p>}
        </div>

        {/* Category Breakdown */}
        <div className="glass-card p-6 animate-fade-up" style={{ animationDelay: '50ms' }} data-testid="chart-category">
          <div className="flex items-center gap-2 mb-5">
            <CurrencyDollar size={18} weight="duotone" className="text-[#00E5FF]" />
            <span className="text-sm font-semibold text-white">Category Breakdown</span>
          </div>
          {data.category_breakdown?.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={220}>
                <PieChart>
                  <Pie data={data.category_breakdown} dataKey="amount" nameKey="category" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}>
                    {data.category_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1.5">
                {data.category_breakdown.slice(0, 6).map((c, i) => (
                  <div key={c.category} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-neutral-300">{c.category}</span>
                    </div>
                    <span className="text-neutral-400 font-mono">{cs}{c.amount.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-neutral-500 text-sm text-center py-10">No data</p>}
        </div>

        {/* Budget vs Actual */}
        <div className="glass-card p-6 animate-fade-up" style={{ animationDelay: '100ms' }} data-testid="chart-budget">
          <div className="flex items-center gap-2 mb-5">
            <ChartBar size={18} weight="duotone" className="text-[#22C55E]" />
            <span className="text-sm font-semibold text-white">Budget vs Actual ({data.current_month})</span>
          </div>
          {data.budget_vs_actual?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.budget_vs_actual} barGap={4}>
                <CartesianGrid stroke="#ffffff08" />
                <XAxis dataKey="category" tick={{ fill: '#737373', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#737373', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltipContent />} />
                <Bar dataKey="budget" name="Budget" fill="#22C55E" radius={[4, 4, 0, 0]} opacity={0.4} />
                <Bar dataKey="spent" name="Spent" fill="#FF6B35" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-neutral-500 text-sm text-center py-10">No budgets set for this month</p>}
        </div>

        {/* Staff Attendance */}
        <div className="glass-card p-6 animate-fade-up" style={{ animationDelay: '150ms' }} data-testid="chart-attendance">
          <div className="flex items-center gap-2 mb-5">
            <UsersThree size={18} weight="duotone" className="text-[#A855F7]" />
            <span className="text-sm font-semibold text-white">Staff Attendance ({data.current_month})</span>
          </div>
          {data.staff_attendance?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.staff_attendance} layout="vertical" barGap={2}>
                <CartesianGrid stroke="#ffffff08" />
                <XAxis type="number" tick={{ fill: '#737373', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#d4d4d4', fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
                <Tooltip content={<ChartTooltipContent />} />
                <Bar dataKey="present" name="Present" fill="#22C55E" radius={[0, 4, 4, 0]} stackId="a" />
                <Bar dataKey="absent" name="Absent" fill="#EF4444" radius={[0, 4, 4, 0]} stackId="a" opacity={0.3} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-neutral-500 text-sm text-center py-10">No staff data</p>}
        </div>

        {/* Energy Usage */}
        <div className="glass-card p-6 animate-fade-up" style={{ animationDelay: '200ms' }} data-testid="chart-energy">
          <div className="flex items-center gap-2 mb-5">
            <Lightning size={18} weight="duotone" className="text-[#F59E0B]" />
            <span className="text-sm font-semibold text-white">Energy Costs (6 months)</span>
          </div>
          {data.energy_trend?.some(e => e.electricity || e.gas || e.water) ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.energy_trend}>
                <CartesianGrid stroke="#ffffff08" />
                <XAxis dataKey="label" tick={{ fill: '#737373', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#737373', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltipContent />} />
                <Line type="monotone" dataKey="electricity" stroke="#F59E0B" strokeWidth={2} dot={false} name="Electricity" />
                <Line type="monotone" dataKey="gas" stroke="#FF6B35" strokeWidth={2} dot={false} name="Gas" />
                <Line type="monotone" dataKey="water" stroke="#00E5FF" strokeWidth={2} dot={false} name="Water" />
                <Legend wrapperStyle={{ fontSize: 11, color: '#737373' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-neutral-500 text-sm text-center py-10">No energy data</p>}
        </div>

        {/* Loan Summary */}
        <div className="glass-card p-6 animate-fade-up" style={{ animationDelay: '250ms' }} data-testid="chart-loans">
          <div className="flex items-center gap-2 mb-5">
            <Bank size={18} weight="duotone" className="text-[#EC4899]" />
            <span className="text-sm font-semibold text-white">Active Loan Balances</span>
          </div>
          {data.loan_summary?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.loan_summary} layout="vertical">
                <CartesianGrid stroke="#ffffff08" />
                <XAxis type="number" tick={{ fill: '#737373', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="staff" tick={{ fill: '#d4d4d4', fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
                <Tooltip content={<ChartTooltipContent />} />
                <Bar dataKey="paid" name="Paid" fill="#22C55E" radius={[0, 0, 0, 0]} stackId="a" />
                <Bar dataKey="remaining" name="Remaining" fill="#F59E0B" radius={[0, 4, 4, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-neutral-500 text-sm text-center py-10">No active loans</p>}
        </div>

        {/* Member Spending */}
        {data.member_spending?.length > 0 && (
          <div className="lg:col-span-2 glass-card p-6 animate-fade-up" style={{ animationDelay: '300ms' }} data-testid="chart-member-spending">
            <div className="flex items-center gap-2 mb-5">
              <Wallet size={18} weight="duotone" className="text-[#3B82F6]" />
              <span className="text-sm font-semibold text-white">Member Spending vs Allowance ({data.current_month})</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.member_spending} barGap={4}>
                <CartesianGrid stroke="#ffffff08" />
                <XAxis dataKey="name" tick={{ fill: '#d4d4d4', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#737373', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltipContent />} />
                <Bar dataKey="allowance" name="Allowance" fill="#3B82F6" radius={[4, 4, 0, 0]} opacity={0.3} />
                <Bar dataKey="spent" name="Spent" fill="#FF6B35" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
